import React, { useState, useEffect } from 'react';

import {
  COLOR_SCALE,
  SIZE_SCALE,
  MARGIN_BOTTOM,
  TYPE_SCALE,
  MARGIN_AUTO,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useTranslation } from '@/hooks/useTranslation';
import {
  buildCloudflareCDNUrl,
  isCloudflareCDNUrl,
  extractOriginalUrlFromCDN,
} from '@/utils/supabase/imageTransforms';

/**
 * ProgressiveImage - Image component with blur-up effect and Cloudflare CDN optimization
 * Loads a low-quality placeholder first, then transitions to full resolution
 */
interface ProgressiveImageProps {
  src: string;
  placeholder?: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  objectFit?: React.CSSProperties['objectFit'];
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'jpeg' | 'png' | 'avif';
  useCDN?: boolean;
  onLoad?: () => void;
}

// Helper function to create CDN optimized URL
const createOptimizedSrc = (
  imageSrc: string,
  useCDN: boolean,
  width?: number,
  height?: number,
  quality?: number,
  format?: 'auto' | 'webp' | 'jpeg' | 'png' | 'avif',
  blur: number = 0
) => {
  if (!useCDN || !imageSrc || isCloudflareCDNUrl(imageSrc)) {
    return imageSrc;
  }

  return buildCloudflareCDNUrl(imageSrc, {
    width: width ?? 0,
    height: height ?? 0,
    quality: quality ?? (blur > 0 ? 50 : 90),
    format: format ?? 'auto',
    fit: 'scale-down',
    gravity: 'auto',
    background: 'transparent',
    sharpen: false,
    blur,
    metadata: 'none',
  });
};

// Helper function to create placeholder
const createPlaceholder = (placeholder: string | undefined, useCDN: boolean) => {
  if (!placeholder) return null;
  return createOptimizedSrc(placeholder, useCDN, 50, 0, 50, 'auto', 10);
};

// Custom hook to handle image loading logic
const useImageLoading = (
  optimizedSrc: string,
  optimizedPlaceholder: string | null,
  onLoad?: () => void
) => {
  const [imgSrc, setImgSrc] = useState(optimizedPlaceholder ?? optimizedSrc);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    // Reset state when src changes
    setIsLoading(true);
    setIsError(false);
    setImgSrc(optimizedPlaceholder ?? optimizedSrc);

    // Create a new image to preload the full resolution
    const img = new Image();

    img.onload = () => {
      setImgSrc(optimizedSrc);
      setIsLoading(false);
      if (onLoad) onLoad();
    };

    img.onerror = () => {
      // Try to fallback to original URL if this is a CDN URL
      if (isCloudflareCDNUrl(optimizedSrc)) {
        const originalUrl = extractOriginalUrlFromCDN(optimizedSrc);
        if (originalUrl && originalUrl !== img.src) {
          // Try loading the original URL
          const fallbackImg = new Image();
          fallbackImg.onload = () => {
            setImgSrc(originalUrl);
            setIsLoading(false);
            setIsError(false);
            if (onLoad) onLoad();
          };
          fallbackImg.onerror = () => {
            setIsError(true);
            setIsLoading(false);
          };
          fallbackImg.src = originalUrl;
          return;
        }
      }

      setIsError(true);
      setIsLoading(false);
    };

    img.src = optimizedSrc;

    // Cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [optimizedSrc, optimizedPlaceholder, onLoad]);

  return { imgSrc, isLoading, isError };
};

function ProgressiveImage(props: ProgressiveImageProps) {
  const {
    src,
    placeholder,
    alt = '',
    className = '',
    style = {},
    objectFit = 'cover',
    width,
    height,
    quality,
    format,
    useCDN = true,
    onLoad,
    ...restProps
  } = props;

  // Transform URLs to use Cloudflare CDN if enabled
  const optimizedSrc = createOptimizedSrc(src, useCDN, width, height, quality, format);

  // Create a low-quality placeholder using CDN with blur
  const optimizedPlaceholder = createPlaceholder(placeholder, useCDN);

  const { imgSrc, isLoading, isError } = useImageLoading(
    optimizedSrc,
    optimizedPlaceholder,
    onLoad
  );
  const { t } = useTranslation();

  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      {/* Placeholder or main image */}
      <img
        src={imgSrc}
        alt={alt}
        className={`w-full h-full transition-all duration-500 ${
          isLoading ? 'blur-sm scale-105' : 'blur-0 scale-100'
        }`}
        style={{
          objectFit,
          ...style,
        }}
        {...restProps}
      />

      {/* Loading overlay - subtle gradient */}
      {isLoading && (
        <div
          className={`absolute inset-0 ${COLOR_SCALE['skeleton-bg-light']} via-${COLOR_SCALE['surface-muted']} to-${COLOR_SCALE['skeleton-bg-light']} shimmer-effect`}
        />
      )}

      {/* Error state - show a subtle error indicator */}
      {isError && (
        <div
          className={`absolute inset-0 ${COLOR_SCALE['skeleton-bg']} flex items-center justify-center`}
        >
          <div className={`text-center ${COLOR_SCALE['foreground-muted']}`}>
            <svg
              className={`${SIZE_SCALE['12']} ${MARGIN_AUTO} ${MARGIN_BOTTOM['2']}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className={TYPE_SCALE['2xs']}>
              {t('common', 'image_unavailable') || 'Image unavailable'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const ProgressiveImageWithMonitor = withMemoryLeakMonitor(ProgressiveImage, {
  componentName: 'ProgressiveImage',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: false, // Image loading can cause multiple renders
  renderThreshold: 10,
});

export default ProgressiveImageWithMonitor;
